import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, FaMoneyBillWave, FaSpinner, FaPlus, FaTimes, FaCheck, FaClock,
  FaChartBar, FaRupeeSign, FaUserPlus, FaImage, FaPalette, FaBuilding,
  FaTimesCircle, FaArrowUp, FaArrowDown
} from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrowthPartnerDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [myUsers, setMyUsers] = useState([]);
  const [salesData, setSalesData] = useState({});
  const [branding, setBranding] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [message, setMessage] = useState(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchAll = useCallback(async () => {
    try {
      const headers = getHeaders();
      const [statsRes, productsRes, usersRes, salesRes, brandingRes] = await Promise.all([
        axios.get(`${API}/growth-partner/dashboard-stats`, { headers }),
        axios.get(`${API}/growth-partner/products`, { headers }),
        axios.get(`${API}/growth-partner/my-users`, { headers }),
        axios.get(`${API}/growth-partner/sales-summary`, { headers }),
        axios.get(`${API}/growth-partner/branding`, { headers })
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setMyUsers(usersRes.data);
      setSalesData(salesRes.data);
      setBranding(brandingRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  if (loading) return <div className="flex items-center justify-center h-96"><FaSpinner className="animate-spin text-[#FFD700] text-4xl" /></div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaChartBar },
    { id: 'users', label: 'My Users', icon: FaUsers },
    { id: 'sales', label: 'Sales & Income', icon: FaMoneyBillWave },
    { id: 'products', label: 'Products', icon: FaRupeeSign },
    { id: 'branding', label: 'Branding', icon: FaPalette },
  ];

  return (
    <div className="space-y-6" data-testid="growth-partner-dashboard">
      {/* Header with Branding */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center gap-4">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="Logo" className="w-14 h-14 rounded-xl object-cover border-2 border-[#FFD700]/30" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
              <FaBuilding className="text-[#0A1929] text-xl" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">{branding.company_name || `Welcome, ${user?.name}`}</h1>
            <p className="text-gray-400 text-sm">{branding.tagline || 'Growth Partner Dashboard'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-[#FFD700] text-[#0A1929]'
                : 'bg-[#1A2942] text-gray-400 hover:text-white border border-[#2D5A8F]'
            }`}
            data-testid={`gp-tab-${tab.id}`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} salesData={salesData} />}
      {activeTab === 'users' && <UsersTab users={myUsers} products={products} showAddUser={showAddUser} setShowAddUser={setShowAddUser} setMessage={setMessage} onRefresh={fetchAll} />}
      {activeTab === 'sales' && <SalesTab salesData={salesData} />}
      {activeTab === 'products' && <ProductsTab products={products} />}
      {activeTab === 'branding' && <BrandingTab branding={branding} setMessage={setMessage} onRefresh={fetchAll} />}
    </div>
  );
};

// ==================== OVERVIEW TAB ====================
const OverviewTab = ({ stats, salesData }) => {
  const statCards = [
    { label: 'Total Users Added', value: stats.total_users || 0, icon: FaUsers, color: 'from-blue-500 to-cyan-500' },
    { label: 'Approved (Sales)', value: stats.approved_users || 0, icon: FaCheck, color: 'from-green-500 to-emerald-500' },
    { label: 'Pending Approval', value: stats.pending_users || 0, icon: FaClock, color: 'from-amber-500 to-yellow-500' },
    { label: 'Total Earnings', value: `₹${(stats.total_income || 0).toLocaleString('en-IN')}`, icon: FaRupeeSign, color: 'from-[#FFD700] to-[#FFA500]' },
    { label: 'This Month', value: `₹${(stats.current_month_income || 0).toLocaleString('en-IN')}`, icon: FaMoneyBillWave, color: 'from-purple-500 to-violet-500' },
    { label: 'Rejected', value: stats.rejected_users || 0, icon: FaTimesCircle, color: 'from-red-500 to-rose-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
          <div key={i} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-4">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${card.color} flex items-center justify-center mb-3`}>
              <card.icon className="text-white" />
            </div>
            <p className="text-white text-xl font-bold">{card.value}</p>
            <p className="text-gray-500 text-xs mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Monthly Income Chart */}
      {salesData.monthly_income && salesData.monthly_income.length > 0 && (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-6">
          <h3 className="text-white font-bold mb-4">Monthly Income</h3>
          <div className="space-y-3">
            {salesData.monthly_income.slice(0, 6).map((m, i) => {
              const maxIncome = Math.max(...salesData.monthly_income.map(x => x.income));
              const pct = maxIncome > 0 ? (m.income / maxIncome) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm w-20 flex-shrink-0">{m.month}</span>
                  <div className="flex-1 bg-[#1A2942] rounded-full h-6 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 5)}%` }}>
                      <span className="text-[#0A1929] text-xs font-bold">₹{m.income.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Sales */}
      {salesData.sales_details && salesData.sales_details.length > 0 && (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-6">
          <h3 className="text-white font-bold mb-4">Recent Sales</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-[#2D5A8F]/50">
                  <th className="text-left py-2 px-3">User</th>
                  <th className="text-left py-2 px-3">Product</th>
                  <th className="text-right py-2 px-3">MRP</th>
                  <th className="text-right py-2 px-3">Base Price</th>
                  <th className="text-right py-2 px-3">Commission</th>
                  <th className="text-left py-2 px-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {salesData.sales_details.slice(0, 10).map((s, i) => (
                  <tr key={i} className="border-b border-[#2D5A8F]/30 hover:bg-[#1A2942]/50">
                    <td className="py-2 px-3 text-white">{s.user_name}</td>
                    <td className="py-2 px-3 text-gray-300">{s.product_name}</td>
                    <td className="py-2 px-3 text-gray-400 text-right">₹{s.mrp}</td>
                    <td className="py-2 px-3 text-gray-400 text-right">₹{s.base_price}</td>
                    <td className="py-2 px-3 text-[#FFD700] text-right font-medium">₹{s.commission_earned}</td>
                    <td className="py-2 px-3 text-gray-500">{s.approved_at ? new Date(s.approved_at).toLocaleDateString('en-IN') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== USERS TAB ====================
const UsersTab = ({ users, products, showAddUser, setShowAddUser, setMessage, onRefresh }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'advisor', password: '', product_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/growth-partner/add-user`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCreatedUser(res.data);
      setMessage({ type: 'success', text: res.data.message });
      setForm({ name: '', email: '', phone: '', role: 'advisor', password: '', product_id: '' });
      onRefresh();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add user' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors = {
    'pending_approval': 'bg-amber-500/20 text-amber-400',
    'approved': 'bg-green-500/20 text-green-400',
    'rejected': 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold text-lg">My Advisors & Managers</h3>
        <button onClick={() => { setShowAddUser(true); setCreatedUser(null); }} className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg text-sm font-bold hover:bg-[#FFA500] transition-colors" data-testid="gp-add-user-btn">
          <FaUserPlus /> Add User
        </button>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold text-lg">{createdUser ? 'User Created!' : 'Add New User'}</h3>
              <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-white"><FaTimes /></button>
            </div>

            {createdUser ? (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-400 font-medium">{createdUser.message}</p>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4 space-y-2">
                  <p className="text-gray-300 text-sm"><strong>Name:</strong> {createdUser.user.name}</p>
                  <p className="text-gray-300 text-sm"><strong>Email:</strong> {createdUser.credentials.email}</p>
                  <p className="text-gray-300 text-sm"><strong>Password:</strong> <span className="text-[#FFD700] font-mono">{createdUser.credentials.temporary_password}</span></p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-amber-400 text-sm">This user is pending Super Admin approval. The sale will be counted once approved.</p>
                </div>
                <button onClick={() => setShowAddUser(false)} className="w-full py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs">Full Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Phone</label>
                  <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Role *</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm">
                    <option value="advisor">Advisor</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Product *</label>
                  <select value={form.product_id} onChange={e => setForm({...form, product_id: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Select Product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} (MRP: ₹{p.mrp})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Password (min 6 chars, or auto-generated)</label>
                  <input value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <button type="submit" disabled={submitting} className="w-full py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold disabled:opacity-50">
                  {submitting ? 'Adding...' : 'Add User'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Users List */}
      {users.length === 0 ? (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-10 text-center">
          <FaUsers className="text-gray-600 text-4xl mx-auto mb-3" />
          <p className="text-gray-400">No users added yet. Click "Add User" to get started.</p>
        </div>
      ) : (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-[#2D5A8F]/50 bg-[#1A2942]/50">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Phone</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-left py-3 px-4">Product</th>
                  <th className="text-left py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-[#2D5A8F]/30 hover:bg-[#1A2942]/30">
                    <td className="py-3 px-4 text-white font-medium">{u.name}</td>
                    <td className="py-3 px-4 text-gray-300">{u.email}</td>
                    <td className="py-3 px-4 text-gray-400">{u.phone || '-'}</td>
                    <td className="py-3 px-4"><span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">{u.role}</span></td>
                    <td className="py-3 px-4 text-gray-300">{u.product_name || '-'}</td>
                    <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs ${statusColors[u.status] || 'bg-gray-500/20 text-gray-400'}`}>{u.status?.replace('_', ' ')}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SALES TAB ====================
const SalesTab = ({ salesData }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-5">
          <p className="text-gray-500 text-sm">Total Sales</p>
          <p className="text-3xl font-bold text-white mt-1">{salesData.total_sales || 0}</p>
        </div>
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-5">
          <p className="text-gray-500 text-sm">Total Income</p>
          <p className="text-3xl font-bold text-[#FFD700] mt-1">₹{(salesData.total_income || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-5">
          <p className="text-gray-500 text-sm">Avg. per Sale</p>
          <p className="text-3xl font-bold text-white mt-1">₹{salesData.total_sales > 0 ? Math.round(salesData.total_income / salesData.total_sales).toLocaleString('en-IN') : 0}</p>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {salesData.monthly_income && salesData.monthly_income.length > 0 && (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-6">
          <h3 className="text-white font-bold mb-4">Monthly Income Breakdown</h3>
          <div className="space-y-2">
            {salesData.monthly_income.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#2D5A8F]/20">
                <span className="text-gray-300">{m.month}</span>
                <span className="text-[#FFD700] font-bold">₹{m.income.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sales Details */}
      {salesData.sales_details && salesData.sales_details.length > 0 && (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-6">
          <h3 className="text-white font-bold mb-4">All Sales</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-[#2D5A8F]/50">
                  <th className="text-left py-2 px-3">User</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-left py-2 px-3">Product</th>
                  <th className="text-right py-2 px-3">MRP</th>
                  <th className="text-right py-2 px-3">Base (excl. GST)</th>
                  <th className="text-right py-2 px-3">Comm %</th>
                  <th className="text-right py-2 px-3">Earned</th>
                  <th className="text-left py-2 px-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {salesData.sales_details.map((s, i) => (
                  <tr key={i} className="border-b border-[#2D5A8F]/30">
                    <td className="py-2 px-3 text-white">{s.user_name}</td>
                    <td className="py-2 px-3 text-gray-400">{s.role}</td>
                    <td className="py-2 px-3 text-gray-300">{s.product_name}</td>
                    <td className="py-2 px-3 text-gray-400 text-right">₹{s.mrp}</td>
                    <td className="py-2 px-3 text-gray-400 text-right">₹{s.base_price}</td>
                    <td className="py-2 px-3 text-gray-400 text-right">{s.commission_percent}%</td>
                    <td className="py-2 px-3 text-[#FFD700] text-right font-medium">₹{s.commission_earned}</td>
                    <td className="py-2 px-3 text-gray-500">{s.approved_at ? new Date(s.approved_at).toLocaleDateString('en-IN') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(!salesData.sales_details || salesData.sales_details.length === 0) && (
        <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-10 text-center">
          <FaMoneyBillWave className="text-gray-600 text-4xl mx-auto mb-3" />
          <p className="text-gray-400">No sales yet. Add users and get them approved to start earning.</p>
        </div>
      )}
    </div>
  );
};

// ==================== PRODUCTS TAB ====================
const ProductsTab = ({ products }) => (
  <div className="space-y-4">
    <h3 className="text-white font-bold text-lg">Available Products</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {products.map(p => {
        const base = Math.round(p.mrp / 1.18);
        const comm = Math.round(base * p.commission_percent / 100);
        return (
          <div key={p.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-5 hover:border-[#FFD700]/30 transition-colors">
            <h4 className="text-white font-bold text-base mb-3">{p.name}</h4>
            <p className="text-gray-500 text-xs mb-4">{p.description}</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500 text-sm">MRP</span>
                <span className="text-white font-medium">₹{p.mrp.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 text-sm">Base (excl. 18% GST)</span>
                <span className="text-gray-300">₹{base.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 text-sm">Commission</span>
                <span className="text-[#FFD700] font-bold">{p.commission_percent}%</span>
              </div>
              <div className="border-t border-[#2D5A8F]/30 pt-2 mt-2 flex justify-between">
                <span className="text-gray-500 text-sm">You Earn Per Sale</span>
                <span className="text-green-400 font-bold">₹{comm.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ==================== BRANDING TAB ====================
const BrandingTab = ({ branding, setMessage, onRefresh }) => {
  const [form, setForm] = useState({
    company_name: branding.company_name || '',
    tagline: branding.tagline || '',
    logo_url: branding.logo_url || '',
    primary_color: branding.primary_color || '#FFD700'
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/growth-partner/branding`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMessage({ type: 'success', text: 'Branding updated successfully!' });
      onRefresh();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update branding' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <h3 className="text-white font-bold text-lg">Branding Settings</h3>
      <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-6 space-y-4">
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Company Name</label>
          <input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2.5 text-white" placeholder="Your Company Name" data-testid="branding-company-name" />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Tagline</label>
          <input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2.5 text-white" placeholder="Your company tagline" data-testid="branding-tagline" />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Logo URL</label>
          <input value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})} className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2.5 text-white" placeholder="https://your-logo-url.com/logo.png" data-testid="branding-logo-url" />
          {form.logo_url && <img src={form.logo_url} alt="Logo preview" className="mt-3 w-20 h-20 rounded-lg object-cover border border-[#2D5A8F]" />}
        </div>
        <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold hover:bg-[#FFA500] disabled:opacity-50 transition-colors" data-testid="save-branding-btn">
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </div>
    </div>
  );
};

export default GrowthPartnerDashboard;
